import React, { useEffect, useRef, useState } from "react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";

export type DrawerProps = {
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  padding?: number;
  width?: number;
};

/**
 * @param children - The content of the drawer
 * @param open - Whether the drawer is open or not
 * @param onOpenChange - Event handler called when the open state of the dialog changes, should change "open" state
 * @param padding - Padding of the drawer, if undefined it will be set to 0
 * @param width - Wanted width of the drawer, if undefined it will be set to 400px
 */
const Drawer = ({ children, open, onOpenChange, padding, width }: DrawerProps) => {
  const [shouldRender, setShouldRender] = useState(open);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const skipDefaultClose = useRef(false);

  useEffect(() => {
    if (!dialogRef.current) return;
    if (open) {
      setShouldRender(true);

      // If state has desynced somehow (should just be from hot-reload)
      if (dialogRef.current.hasAttribute("open")) {
        console.warn("Drawer is already open. Closing it before opening again.");
        // Close it first so if it's not a modal it can be made one (can't do .showModal() if it's already open)
        // Wish you could detect if it's in modal mode or not but I couldn't find a way (should be possible via aria-modal but it didn't work)
        dialogRef.current.close();
      }
      dialogRef.current?.showModal();
    } else {
      // If a browser doesn't support overlay, the exit transition looks very bad
      // & onTransitionEnd will not run, therefore we need to skip the animation all together
      if (!CSS.supports("overlay", "auto")) {
        setShouldRender(false);
      }
      dialogRef.current.close();
    }
  }, [open]);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      skipDefaultClose.current = true;
      onOpenChange(false);
    }
  };

  return (
    <DrawerContainer
      onMouseDown={handleBackgroundClick} // Not using onClick so if user selects text and drags into the background area it won't trigger
      onClose={(e) => {
        e.stopPropagation(); // Allows multiple dialog using components to be nested

        // onOpenChange is called when the drawer is closed, but we want to skip it if the close was
        // triggered by a background click as otherwise it will be called twice
        if (skipDefaultClose.current) {
          skipDefaultClose.current = false;
          return;
        }
        onOpenChange(false);
      }}
      onClick={(e) => e.stopPropagation()} // Stop click events from propagating to the parent
      ref={dialogRef}
      onTransitionEnd={(e) => {
        if (!open && e.target === e.currentTarget) {
          setShouldRender(false);
        }
      }}
      aria-hidden={shouldRender ? undefined : true} // aria-hidden=false isn't treated correct by some software, it just needs to not be present
      data-drawer=""
    >
      {shouldRender ? (
        <>
          <DrawerBackground onMouseDown={handleBackgroundClick} />
          <DrawerWrapper width={width} padding={padding}>
            {children}
          </DrawerWrapper>
        </>
      ) : null}
    </DrawerContainer>
  );
};

const fadeInBackground = keyframes({
  from: { opacity: 0 },
});
const fadeOutBackground = keyframes({
  to: { opacity: 0 },
});

const slideIn = keyframes({
  from: { transform: "translate3d(100%, 0, 0)" },
});
const slideOut = keyframes({
  to: { transform: "translate3d(100%, 0, 0)" },
});

// We need to have our own background instead of ::backdrop because otherwise toasts will be centered on the drawer instead of the screen
// which we don't want due to the small size of some drawers
const DrawerBackground = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.sysBackgroundOverlay,
  opacity: 0.5,
  position: "fixed",
  width: "100vw",
  height: "100vh",
  zIndex: -1,

  "dialog[open] > &": {
    animation: `${fadeInBackground} 300ms ease-in-out`,
  },
  "dialog:not([open]) > &": {
    "@supports (overlay: auto)": {
      animation: `${fadeOutBackground} 300ms ease-in-out`,
    },
  },
}));

const DrawerWrapper = styled.div<{ width?: number; padding?: number }>(
  ({ width = 400, padding, theme }) => ({
    // Need to take up full height + width of the dialog
    boxSizing: "border-box",
    padding: padding,
    height: "100vh",
    width: width,
    maxWidth: width,

    backgroundColor: theme.colors.sysBackgroundDefault,
    boxShadow: "-6px 0 16px 2px rgba(0, 0, 0, 0.12)",
    overflow: "auto", // Allow scrolling if needed
    scrollbarColor: "initial", // TODO: Get rid of after antd5 table is replaced (inline save button)

    "dialog[open] > &": {
      animation: `${slideIn} 300ms ease-out`,
    },
    "dialog:not([open]) > &": {
      "@supports (overlay: auto)": {
        animation: `${slideOut} 300ms ease-in`,
      },
    },
  }),
);

const DrawerContainer = styled.dialog({
  // Disabling margin, border + padding so it doesn't trigger onMouseDown from clicking on them
  border: 0,
  padding: 0,
  margin: 0,

  // We need to manage the background ourselves so take up the whole screen
  minWidth: "100vw",
  maxHeight: "none", // https://til.simonwillison.net/css/dialog-full-height
  cursor: "auto", // Cursor might be set on the parent of the Drawer but shouldn't go to the drawer contents

  // Disable background on this component + on the inbuilt backdrop
  backgroundColor: "transparent",
  overflow: "hidden",
  "::backdrop": {
    display: "none",
  },

  // Put drawer on the right side of the screen
  display: "flex",
  flexDirection: "row-reverse",

  // Need to animation display & overlay to allow the close animation to play
  // conditional is to disable it if overlay animation isn't supported by the browser
  "@supports (overlay: auto)": {
    transition: "display 300ms allow-discrete, overlay 300ms allow-discrete",
  },
});

export default Drawer;
